"use client";

import { useTranslations } from "next-intl";
import type { ComponentProps } from "react";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/cn";

/** How many pages fit before the run is elided. */
const MAX_PAGES_WITHOUT_ELLIPSIS = 4;

function PaginationButton({ className, ...props }: ComponentProps<typeof Button>) {
  return (
    <Button
      variant="outline"
      className={cn(
        "rounded-xl px-3 hover:border-primary hover:bg-primary",
        '[&[aria-current="page"]]:border-primary',
        className
      )}
      {...props}
    />
  );
}

/**
 * The page numbers to show: always the first and last, the current page with a
 * neighbour either side, and an ellipsis wherever that skips a run.
 *
 * Split out from the rendering because it's the part with the edge cases —
 * fewer pages than the window, the current page adjacent to either end — and
 * those are worth testing without a DOM.
 */
export function paginationRange(
  currentPage: number,
  totalPages: number
): Array<number | "ellipsis"> {
  if (!Number.isFinite(totalPages) || !Number.isFinite(currentPage)) return [];
  if (totalPages < 1) return [];

  if (totalPages <= MAX_PAGES_WITHOUT_ELLIPSIS) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const range: Array<number | "ellipsis"> = [1];

  if (currentPage > 3) range.push("ellipsis");

  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);
  for (let page = start; page <= end; page++) range.push(page);

  if (currentPage < totalPages - 2) range.push("ellipsis");

  range.push(totalPages);
  return range;
}

/** Two chevrons, for the jump to the first or last page. */
function DoubleChevron({ direction }: { direction: "left" | "right" }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className={direction === "right" ? "rotate-180" : undefined}
    >
      <path
        d="M11.7267 12L12.6667 11.06L9.61341 8L12.6667 4.94L11.7267 4L7.72675 8L11.7267 12Z"
        fill="currentColor"
      />
      <path
        d="M7.33344 12L8.27344 11.06L5.2201 8L8.27344 4.94L7.33344 4L3.33344 8L7.33344 12Z"
        fill="currentColor"
      />
    </svg>
  );
}

export function Pagination({
  currentPage,
  totalPages,
  setPage,
}: {
  currentPage: number;
  totalPages: number;
  setPage: (page: number) => void;
}) {
  const t = useTranslations("items.pagination");
  const pages = paginationRange(currentPage, totalPages);

  if (pages.length === 0) return null;

  const isFirst = currentPage <= 1;
  const isLast = currentPage >= totalPages;

  return (
    <nav className="pagination grid items-center gap-2" aria-label={t("label")}>
      <div className="flex space-x-2">
        <PaginationButton
          onClick={() => setPage(1)}
          disabled={isFirst}
          title={t("firstPage")}
          aria-label={t("firstPage")}
        >
          <DoubleChevron direction="left" />
        </PaginationButton>

        <PaginationButton
          onClick={() => setPage(currentPage - 1)}
          disabled={isFirst}
          title={t("previousPage")}
          aria-label={t("previousPage")}
        >
          <i
            className="icon ic-material_ic_keyboard_arrow_left_48px"
            aria-hidden="true"
          />
        </PaginationButton>
      </div>

      <div className="flex items-center justify-center space-x-2">
        {pages.map((page, index) =>
          page === "ellipsis" ? (
            <span key={`ellipsis-${index}`} className="p-2" aria-hidden="true">
              ...
            </span>
          ) : (
            <PaginationButton
              key={page}
              onClick={() => setPage(page)}
              aria-current={currentPage === page ? "page" : undefined}
              aria-label={t("page", { page })}
            >
              {page}
            </PaginationButton>
          )
        )}
      </div>

      <div className="flex justify-end space-x-2">
        <PaginationButton
          onClick={() => setPage(currentPage + 1)}
          disabled={isLast}
          title={t("nextPage")}
          aria-label={t("nextPage")}
        >
          <i
            className="icon ic-material_ic_keyboard_arrow_right_48px"
            aria-hidden="true"
          />
        </PaginationButton>

        <PaginationButton
          onClick={() => setPage(totalPages)}
          disabled={isLast}
          title={t("lastPage")}
          aria-label={t("lastPage")}
        >
          <DoubleChevron direction="right" />
        </PaginationButton>
      </div>
    </nav>
  );
}
